import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SoftwareCard from '@/components/feature/SoftwareCard';
import { categories } from '@/mocks/categories';
import { softwares } from '@/mocks/softwares';
import useDocumentMeta from '@/hooks/useDocumentMeta';

type SortKey = 'relevance' | 'latest' | 'downloads';

export default function SearchPage() {
  const [params] = useSearchParams();
  const initialKeyword = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(initialKeyword);
  const [activeCategory, setActiveCategory] = useState('all');
  const [sort, setSort] = useState<SortKey>('relevance');

  useDocumentMeta({
    title: initialKeyword
      ? `“${initialKeyword}”搜索结果 - 黄金网站软件app免费`
      : '搜索软件 - 黄金网站软件app免费',
    description:
      '在黄金网站软件app免费中搜索黄金投资软件、行情APP与理财工具，快速找到所需的软件资源。',
    keywords: '黄金网站软件app免费,黄金投资软件搜索,黄金行情app',
  });

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list = softwares.filter((s) => {
      if (!kw) return true;
      return (
        s.name.toLowerCase().includes(kw) ||
        s.summary.toLowerCase().includes(kw) ||
        s.categoryName.toLowerCase().includes(kw) ||
        s.tags.some((t) => t.toLowerCase().includes(kw))
      );
    });
    if (activeCategory !== 'all') {
      list = list.filter((s) => s.categoryId === activeCategory);
    }
    if (sort === 'latest') {
      list = [...list].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
    } else if (sort === 'downloads') {
      list = [...list].sort((a, b) => b.downloads - a.downloads);
    }
    return list;
  }, [keyword, activeCategory, sort]);

  const hotRecommend = [...softwares].sort((a, b) => b.downloads - a.downloads).slice(0, 3);

  return (
    <SiteLayout>
      <Breadcrumb items={[{ label: '搜索结果' }]} />

      <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">搜索软件</h1>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入软件名称、功能或分类关键词"
              className="w-full h-11 pl-9 pr-3 rounded-md border border-background-200 bg-background-100 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
            />
          </div>
        </div>
        {initialKeyword && (
          <p className="mt-3 text-sm text-foreground-500">
            当前关键词：
            <span className="text-primary-700 font-medium">「{initialKeyword}」</span>
          </p>
        )}
      </div>

      <div className="mt-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
            <i className="ri-filter-3-line text-base" />
          </span>
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            className={`h-8 px-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
              activeCategory === 'all'
                ? 'bg-secondary-950 text-background-50 font-medium'
                : 'bg-background-50 text-foreground-600 border border-background-200'
            }`}
          >
            全部分类
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategory(cat.id)}
              className={`h-8 px-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                activeCategory === cat.id
                  ? 'bg-secondary-950 text-background-50 font-medium'
                  : 'bg-background-50 text-foreground-600 border border-background-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="h-9 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="relevance">相关度</option>
            <option value="latest">最新发布</option>
            <option value="downloads">热门下载</option>
          </select>
        </div>
      </div>

      <p className="mt-4 text-sm text-foreground-500">
        共找到 <span className="text-primary-700 font-medium">{results.length}</span> 个结果
      </p>

      {results.length > 0 ? (
        <div className="mt-4 flex flex-col gap-4">
          {results.map((s) => (
            <SoftwareCard key={s.id} software={s} variant="list" />
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-12 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
            暂无相关软件
          </h3>
          <p className="mt-2 text-sm text-foreground-500">
            没有找到与当前关键词匹配的软件，换个词试试，或看看下面这些热门推荐。
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
            {hotRecommend.map((s) => (
              <SoftwareCard key={s.id} software={s} variant="grid" />
            ))}
          </div>
        </div>
      )}

      <section className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
        <h2 className="font-heading text-base font-bold text-foreground-950">试试这些热门分类</h2>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className="h-9 px-4 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-700 transition-colors flex items-center gap-2"
            >
              <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                <i className={`${cat.icon} text-base`} />
              </span>
              {cat.name}
            </Link>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}